/* jshint esversion: 6 */
import * as pdfjs from 'pdfjs-dist';

if (pdfjs.GlobalWorkerOptions) {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

export default class PdfViewer {
    /**
     * PDFViewer class for loading and displaying PDF document.
     *
     * @param {Object} params - Init parameters
     * @param {Object} params.lib - pdf.js library
     * @param {string} params.url - Pdf document path or url
     * @param {Object} params.ui - UI parameters
     * @param {HTMLElement} params.ui.canvas - Canvas element on which PDF will be rendered/drawn
     * @param {HTMLElement} params.ui.page_count - Span element for displaying total number of PDF pages
     * @param {HTMLElement} params.ui.page_num - Span element for displaying current number of PDF page being viewed
     * @param {HTMLElement} params.ui.btn_page_prev - Button element for calling previous PDF page view
     * @param {HTMLElement} params.ui.btn_page_next - Button element for next PDF page view
     */
    constructor(params) {
        this.url = params.url;
        this.doc = null;
        this.page_num = 1;
        this.page_rendering = false;
        this.page_num_pending = null;
        this.scale = 2.0;

        // UI elements
        this.canvas = params.ui.canvas;
        this.ui_page_count = params.ui.page_count;
        this.ui_page_num = params.ui.page_num;
        this.ui_btn_prev = params.ui.btn_page_prev;
        this.ui_btn_next = params.ui.btn_page_next;

        // Canvas context
        this.ctx = this.canvas.getContext('2d');

        // Event listeners
        this.prev_page = this.prev_page.bind(this);
        this.next_page = this.next_page.bind(this);
        this.ui_btn_prev.addEventListener('click', this.prev_page);
        this.ui_btn_next.addEventListener('click', this.next_page);
    }

    /**
     * Load PDF document and render initial page
     */
    load() {
        pdfjs.getDocument(this.url).promise.then(
            (doc) => {
                this.doc = doc;
                this.ui_page_count.textContent = this.doc.numPages;
                this.render_page(this.page_num);
            },
            (reason) => {
                // eslint-disable-next-line no-console
                console.error('PdfViewer load error', reason);
            },
        );
    }

    /**
     * Render page
     * @param {number} num - Page number
     */
    render_page(num) {
        this.page_rendering = true;
        this.doc.getPage(num).then(
            (page) => {
                const viewport = page.getViewport({ scale: this.scale });
                this.canvas.height = viewport.height;
                this.canvas.width = viewport.width;
                const renderCtx = {
                    canvasContext: this.ctx,
                    viewport,
                };
                const renderTask = page.render(renderCtx);
                renderTask.promise.then(() => {
                    this.page_rendering = false;
                    if (this.page_num_pending !== null) {
                        this.render_page(this.page_num_pending);
                        this.page_num_pending = null;
                    }
                });
            },
            (reason) => {
                // eslint-disable-next-line no-console
                console.error('PdfViewer render page error:', reason);
            },
        );
        this.ui_page_num.textContent = num;
    }

    /**
     * Queue page rendering.
     * If another page rendering is in progress, wait until the rendering is finished.
     * Otherwise, execute immediately
     * @param {number} num - Page number
     */
    queue_render_page(num) {
        if (this.page_rendering) {
            this.page_num_pending = num;
        } else {
            this.render_page(num);
        }
    }

    /**
     * Prev page
     */
    prev_page() {
        if (this.page_num <= 1) {
            return;
        }
        this.page_num--;
        this.queue_render_page(this.page_num);
    }

    /**
     * Next page
     */
    next_page() {
        if (this.page_num >= this.doc.numPages) {
            return;
        }
        this.page_num++;
        this.queue_render_page(this.page_num);
    }
}
